import { Program, Quest, QuestTemplate, Milestone, ProgramSchedule, QuestDifficulty, SubTask, Stats } from '../types';
import { getXPRewardForDifficulty, getStatRewardsForDifficulty, getCurrencyRewardForDifficulty } from '../utils/levelSystem';

class ProgramService {
  // Helper: Calculate days between two dates (CONSOLIDATED)
  private getDaysBetween(date1: Date, date2: Date): number {
    return Math.floor((date2.getTime() - date1.getTime()) / (1000 * 60 * 60 * 24));
  }

  // Helper: Get program start date (CONSOLIDATED)
  private getProgramStartDate(program: Program): Date {
    return new Date(program.startDate);
  }

  // Helper: Generate unique ID (CONSOLIDATED)
  private generateId(prefix: string, index: number = 0): string {
    return `${prefix}_${Date.now()}_${index}`;
  }

  // Calculate which phase (week) a program is currently in
  getCurrentPhase(program: Program): number {
    if (program.status === 'completed') return program.totalPhases;
    if (program.status === 'paused') return program.currentPhase;

    const today = new Date();
    const startDate = this.getProgramStartDate(program);
    const daysSinceStart = this.getDaysBetween(startDate, today);
    
    // Assuming weekly phases (7 days per phase)
    const currentPhase = Math.floor(daysSinceStart / 7) + 1;
    return Math.min(currentPhase, program.totalPhases);
  }

  // 🚀 UNIVERSAL: Get current phase for any program type (AI or Traditional)
  getCurrentPhaseUniversal(program: Program, quests: Quest[]): number {
    if (program.status === 'completed') return program.totalPhases;
    if (program.status === 'paused') return program.currentPhase;

    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    return isAIProgram 
      ? this.getCurrentPhaseForAIProgram(program, quests)
      : this.getCurrentPhase(program);
  }

  // Calculate program progress percentage
  calculateProgress(program: Program, quests?: Quest[]): number {
    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    // For AI programs, use quest completion-based progress
    if (isAIProgram && quests) {
      const programQuests = quests.filter(q => q.programId === program.id && q.scheduledFor);
      const completedQuests = programQuests.filter(q => q.status === 'completed');
      
      if (programQuests.length === 0) return 0;
      
      return Math.min(Math.floor((completedQuests.length / programQuests.length) * 100), 100);
    }

    // For traditional programs, use date-based progress
    const today = new Date();
    const startDate = this.getProgramStartDate(program);
    const endDate = new Date(program.endDate);
    
    if (today >= endDate) return 100;
    
    const totalDuration = endDate.getTime() - startDate.getTime();
    const elapsed = today.getTime() - startDate.getTime();
    
    return Math.min(Math.floor((elapsed / totalDuration) * 100), 100);
  }

  // Check if quests should be generated today based on schedule
  shouldGenerateToday(program: Program, date: Date = new Date()): boolean {
    if (program.status !== 'active') return false;

    const dayOfWeek = date.getDay(); // 0=Sun, 6=Sat
    const schedule = program.schedule;

    switch (schedule.type) {
      case 'daily':
        return true;
      
      case 'every_other_day':
        const startDate = this.getProgramStartDate(program);
        const daysSinceStart = this.getDaysBetween(startDate, date);
        return daysSinceStart % 2 === 0;
      
      case '2x_week':
        return [1, 4].includes(dayOfWeek); // Monday, Thursday
      
      case '3x_week':
        return [1, 3, 5].includes(dayOfWeek); // Monday, Wednesday, Friday
      
      case '5x_week':
        return dayOfWeek >= 1 && dayOfWeek <= 5; // Weekdays
      
      case 'weekends':
        return [0, 6].includes(dayOfWeek); // Saturday, Sunday
      
      case 'custom':
        return schedule.customDays?.includes(dayOfWeek) || false;
      
      default:
        return false;
    }
  }

  // Generate ALL quests for the entire program duration with scheduled dates
  generateAllScheduledQuests(program: Program, userId: string): Quest[] {
    const allQuests: Quest[] = [];
    const startDate = new Date(program.startDate);
    let questIndex = 0;

    // Iterate through each day of the program
    for (let dayOffset = 0; dayOffset < program.duration; dayOffset++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + dayOffset);
      const dateStr = currentDate.toISOString().split('T')[0];

      // Check if quests should be generated for this day
      if (!this.shouldGenerateToday(program, currentDate)) continue;

      // Calculate which phase this day belongs to
      const phase = Math.floor(dayOffset / 7) + 1;

      // Get quest templates for this phase
      const phaseTemplates = program.questTemplates.filter(qt => qt.phase === phase);

      // Limit to questsPerDay
      const templatesToGenerate = phaseTemplates.slice(0, program.schedule.questsPerDay);

      // Create quests for this day
      templatesToGenerate.forEach((template) => {
        const quest = this.createQuestFromTemplate(template, program, userId, questIndex, dateStr);
        allQuests.push(quest);
        questIndex++;
      });
    }

    return allQuests;
  }

  // 🚀 NEW: Generate first week quests directly (without templates)
  generateFirstWeekQuests(program: Program, userId: string): Quest[] {
    console.log('🚀 generateFirstWeekQuests called for program:', program.title);
    console.log('🚀 Program details:', {
      domain: program.domain,
      goal: program.goal,
      duration: program.duration,
      schedule: program.schedule,
      startDate: program.startDate
    });
    
    const allQuests: Quest[] = [];
    const startDate = new Date(program.startDate);
    let questIndex = 0;

    // Generate quests for the first week only (7 days)
    for (let dayOffset = 0; dayOffset < Math.min(7, program.duration); dayOffset++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + dayOffset);
      const dateStr = currentDate.toISOString().split('T')[0];

      console.log(`🚀 Day ${dayOffset + 1}: ${dateStr}, shouldGenerate: ${this.shouldGenerateToday(program, currentDate)}`);

      // Check if quests should be generated for this day
      if (!this.shouldGenerateToday(program, currentDate)) continue;

      // Generate quests for this day based on the program's goal and domain
      for (let i = 0; i < program.schedule.questsPerDay; i++) {
        const quest = this.createFirstWeekQuest(program, userId, questIndex, dateStr, dayOffset, i);
        console.log(`🚀 Generated quest ${questIndex + 1}:`, {
          title: quest.title,
          scheduledFor: quest.scheduledFor,
          subTasks: quest.subTasks?.length || 0,
          subTasksContent: quest.subTasks?.map(st => st.text) || []
        });
        allQuests.push(quest);
        questIndex++;
      }
    }

    console.log(`🚀 Total first week quests generated: ${allQuests.length}`);
    return allQuests;
  }

  // 🚀 NEW: Create a quest for the first week without templates
  private createFirstWeekQuest(
    program: Program, 
    userId: string, 
    questIndex: number, 
    scheduledFor: string,
    dayOffset: number,
    dayQuestIndex: number
  ): Quest {
    const questId = this.generateId('quest', questIndex);
    
    // Generate quest content based on program goal and domain
    const questTitle = this.generateFirstWeekQuestTitle(program, dayOffset);
    const questDescription = this.generateFirstWeekQuestDescription(program, dayOffset);
    const subTasks = this.generateFirstWeekSubTasks(program, dayOffset);

    return {
      id: questId,
      title: questTitle,
      description: questDescription,
      type: 'daily',
      difficulty: 'easy',
      xpReward: 10,
      currencyReward: 5,
      statRewards: {},
      status: 'active',
      resetDaily: true,
      scheduledFor,
      programId: program.id,
      userId,
      completedAt: null,
      createdAt: Date.now(),
      resources: [],
      subTasks
    };
  }

  // 🚀 NEW: Generate quest title for first week
  private generateFirstWeekQuestTitle(program: Program, dayOffset: number): string {
    const domain = program.domain;
    const goal = program.goal;
    
    const firstWeekTitles = {
      'fitness': [
        'Morning Warm-up Routine',
        'Basic Strength Foundation',
        'Cardio Introduction',
        'Flexibility & Mobility',
        'Core Activation',
        'Endurance Building',
        'Recovery & Stretching'
      ],
      'programming': [
        'Environment Setup',
        'Basic Syntax Practice',
        'Variable Fundamentals',
        'Control Structures',
        'Function Basics',
        'Data Types Exploration',
        'Code Organization'
      ],
      'learning': [
        'Foundation Concepts',
        'Basic Principles',
        'Core Fundamentals',
        'Essential Skills',
        'Building Blocks',
        'Key Concepts',
        'Fundamental Practice'
      ],
      'business': [
        'Market Research Basics',
        'Business Plan Foundation',
        'Financial Fundamentals',
        'Marketing Introduction',
        'Operations Overview',
        'Strategy Development',
        'Goal Setting'
      ]
    };

    const titles = firstWeekTitles[domain as keyof typeof firstWeekTitles] || firstWeekTitles['learning'];
    return titles[dayOffset % titles.length];
  }

  // 🚀 NEW: Generate quest description for first week
  private generateFirstWeekQuestDescription(program: Program, dayOffset: number): string {
    const domain = program.domain;
    const goal = program.goal;
    
    return `Start your journey towards "${goal}" with this foundational ${domain} task. This is designed to build your confidence and establish good habits from day one.`;
  }

  // 🚀 NEW: Generate subtasks for first week
  private generateFirstWeekSubTasks(program: Program, dayOffset: number): SubTask[] {
    const domain = program.domain;
    
    const firstWeekSubTasks = {
      'fitness': [
        ['Warm up for 5 minutes', 'Complete 3 sets of basic exercises', 'Cool down and stretch'],
        ['Focus on proper form', 'Complete 10-15 repetitions', 'Rest between sets'],
        ['Start with 10 minutes of cardio', 'Maintain steady pace', 'Track your heart rate'],
        ['Hold each stretch for 30 seconds', 'Focus on major muscle groups', 'Breathe deeply during stretches'],
        ['Engage your core throughout', 'Complete 3 sets of core exercises', 'Focus on quality over quantity'],
        ['Start with 15 minutes of endurance', 'Maintain consistent effort', 'Monitor your breathing'],
        ['Spend 10 minutes stretching', 'Focus on areas that feel tight', 'Practice deep breathing']
      ],
      'programming': [
        ['Set up your development environment', 'Install necessary tools', 'Create your first project folder'],
        ['Write 5 simple programs', 'Practice basic syntax', 'Test each program'],
        ['Declare 10 different variables', 'Use appropriate data types', 'Print their values'],
        ['Write if-else statements', 'Create a simple loop', 'Test your logic'],
        ['Write your first function', 'Call the function', 'Test with different inputs'],
        ['Work with strings and numbers', 'Practice type conversion', 'Handle basic operations'],
        ['Organize your code into files', 'Add comments', 'Test everything works']
      ],
      'learning': [
        ['Read the introduction chapter', 'Take notes on key concepts', 'Summarize what you learned'],
        ['Practice the basic exercises', 'Complete 3 practice problems', 'Review your answers'],
        ['Watch the foundational video', 'Take notes', 'Try the examples yourself'],
        ['Read about core principles', 'Identify 3 key points', 'Think of real-world applications'],
        ['Practice the essential skills', 'Complete 5 exercises', 'Reflect on your progress'],
        ['Study the building blocks', 'Practice with examples', 'Test your understanding'],
        ['Review all concepts learned', 'Create a summary', 'Plan next steps']
      ],
      'business': [
        ['Research your target market', 'Identify 3 key competitors', 'Note market trends'],
        ['Draft a basic business concept', 'Define your value proposition', 'Identify your target customers'],
        ['Create a simple budget outline', 'List expected expenses', 'Estimate potential revenue'],
        ['Research marketing channels', 'Identify 3 marketing strategies', 'Note costs and benefits'],
        ['Map out your business process', 'Identify key operations', 'Note potential bottlenecks'],
        ['Develop a basic strategy', 'Set 3 key objectives', 'Create action steps'],
        ['Set SMART goals for your business', 'Create a timeline', 'Identify success metrics']
      ]
    };

    const subTasks = firstWeekSubTasks[domain as keyof typeof firstWeekSubTasks] || firstWeekSubTasks['learning'];
    const tasks = subTasks[dayOffset % subTasks.length];
    
    console.log(`🚀 Generating subtasks for day ${dayOffset + 1}:`, {
      domain,
      dayOffset,
      tasks,
      subTasksCount: tasks.length
    });
    
    return tasks.map((taskText, index) => ({
      id: this.generateId(`subtask_${dayOffset}`, index),
      text: taskText,
      completed: false,
      completedAt: null
    }));
  }

  // 🚀 NEW: Progressive quest generation - generate quests for specific week
  generateProgressiveQuests(program: Program, userId: string, targetWeek: number = 1): Quest[] {
    const allQuests: Quest[] = [];
    const startDate = new Date(program.startDate);
    let questIndex = 0;

    // Generate quests for the specified week only
    const weekStartDay = (targetWeek - 1) * 7;
    const weekEndDay = Math.min(weekStartDay + 7, program.duration);

    for (let dayOffset = weekStartDay; dayOffset < weekEndDay; dayOffset++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(currentDate.getDate() + dayOffset);
      const dateStr = currentDate.toISOString().split('T')[0];

      // Check if quests should be generated for this day
      if (!this.shouldGenerateToday(program, currentDate)) continue;

      // Generate quests for this day based on the week number
      for (let i = 0; i < program.schedule.questsPerDay; i++) {
        const quest = this.createProgressiveQuest(program, userId, questIndex, dateStr, targetWeek, i);
        allQuests.push(quest);
        questIndex++;
      }
    }

    return allQuests;
  }

  // 🚀 NEW: Create a quest for progressive generation
  private createProgressiveQuest(
    program: Program, 
    userId: string, 
    questIndex: number, 
    scheduledFor: string,
    weekNumber: number,
    dayQuestIndex: number
  ): Quest {
    const questId = this.generateId('quest', questIndex);
    
    // Generate quest content based on program goal, domain, and week
    const questTitle = this.generateProgressiveQuestTitle(program, weekNumber, dayQuestIndex);
    const questDescription = this.generateProgressiveQuestDescription(program, weekNumber, dayQuestIndex);
    const subTasks = this.generateProgressiveSubTasks(program, weekNumber, dayQuestIndex);

    return {
      id: questId,
      title: questTitle,
      description: questDescription,
      type: 'daily',
      difficulty: this.getDifficultyForWeek(weekNumber),
      xpReward: this.getXPRewardForWeek(weekNumber),
      currencyReward: this.getCurrencyRewardForWeek(weekNumber),
      statRewards: this.getStatRewardsForWeek(weekNumber),
      status: 'active',
      resetDaily: true,
      scheduledFor,
      programId: program.id,
      userId,
      completedAt: null,
      createdAt: Date.now(),
      resources: [],
      subTasks
    };
  }

  // 🚀 NEW: Generate quest title for progressive generation
  private generateProgressiveQuestTitle(program: Program, weekNumber: number, dayQuestIndex: number): string {
    const domain = program.domain;
    const goal = program.goal;
    
    const weekTitles = {
      'fitness': {
        1: ['Morning Warm-up Routine', 'Basic Strength Foundation', 'Cardio Introduction', 'Flexibility & Mobility', 'Core Activation', 'Endurance Building', 'Recovery & Stretching'],
        2: ['Intermediate Strength Training', 'Advanced Cardio Workout', 'Power Development', 'Balance & Coordination', 'Muscle Endurance', 'Speed Training', 'Active Recovery'],
        3: ['Advanced Strength Techniques', 'High-Intensity Cardio', 'Explosive Power', 'Complex Movements', 'Athletic Performance', 'Competition Prep', 'Recovery & Regeneration'],
        4: ['Elite Strength Training', 'Peak Performance Cardio', 'Mastery Techniques', 'Professional Skills', 'Championship Prep', 'Elite Conditioning', 'Peak Recovery']
      },
      'programming': {
        1: ['Environment Setup', 'Basic Syntax Practice', 'Variable Fundamentals', 'Control Structures', 'Function Basics', 'Data Types Exploration', 'Code Organization'],
        2: ['Object-Oriented Programming', 'Data Structures', 'Algorithm Basics', 'Error Handling', 'File Operations', 'Debugging Techniques', 'Code Testing'],
        3: ['Advanced Algorithms', 'Design Patterns', 'Database Integration', 'API Development', 'Performance Optimization', 'Security Practices', 'Code Architecture'],
        4: ['System Design', 'Microservices Architecture', 'Cloud Computing', 'DevOps Practices', 'Advanced Security', 'Scalability Solutions', 'Production Deployment']
      },
      'learning': {
        1: ['Foundation Concepts', 'Basic Principles', 'Core Fundamentals', 'Essential Skills', 'Building Blocks', 'Key Concepts', 'Fundamental Practice'],
        2: ['Intermediate Applications', 'Practical Exercises', 'Real-World Examples', 'Problem Solving', 'Critical Thinking', 'Analysis Skills', 'Synthesis Practice'],
        3: ['Advanced Techniques', 'Complex Problem Solving', 'Expert Applications', 'Innovation Methods', 'Advanced Analysis', 'Mastery Practice', 'Expert Synthesis'],
        4: ['Mastery Applications', 'Expert Problem Solving', 'Advanced Innovation', 'Professional Skills', 'Leadership Development', 'Expert Analysis', 'Mastery Synthesis']
      },
      'business': {
        1: ['Market Research Basics', 'Business Plan Foundation', 'Financial Fundamentals', 'Marketing Introduction', 'Operations Overview', 'Strategy Development', 'Goal Setting'],
        2: ['Advanced Market Analysis', 'Financial Planning', 'Marketing Strategy', 'Operations Management', 'Strategic Planning', 'Risk Assessment', 'Performance Metrics'],
        3: ['Market Expansion', 'Financial Optimization', 'Advanced Marketing', 'Operational Excellence', 'Strategic Implementation', 'Risk Management', 'Performance Analysis'],
        4: ['Market Leadership', 'Financial Mastery', 'Marketing Excellence', 'Operational Innovation', 'Strategic Leadership', 'Risk Mitigation', 'Performance Optimization']
      }
    };

    const domainTitles = weekTitles[domain as keyof typeof weekTitles] || weekTitles['learning'];
    const weekTitlesArray = domainTitles[weekNumber as keyof typeof domainTitles] || domainTitles[1];
    return weekTitlesArray[dayQuestIndex % weekTitlesArray.length];
  }

  // 🚀 NEW: Generate quest description for progressive generation
  private generateProgressiveQuestDescription(program: Program, weekNumber: number, dayQuestIndex: number): string {
    const domain = program.domain;
    const goal = program.goal;
    const intensity = program.intensity;
    
    const weekDescriptions = {
      1: `Start your journey towards "${goal}" with this foundational ${domain} task. This is designed to build your confidence and establish good habits from day one.`,
      2: `Continue building your skills in ${domain} with this intermediate-level task. You're now ready to tackle more challenging concepts while maintaining your momentum.`,
      3: `Advance your ${domain} expertise with this challenging task. You've built a solid foundation and are now ready for more complex and sophisticated approaches.`,
      4: `Master your ${domain} skills with this expert-level task. You've demonstrated consistent progress and are now ready for the most advanced techniques and applications.`
    };

    return weekDescriptions[weekNumber as keyof typeof weekDescriptions] || weekDescriptions[1];
  }

  // 🚀 NEW: Generate subtasks for progressive generation
  private generateProgressiveSubTasks(program: Program, weekNumber: number, dayQuestIndex: number): SubTask[] {
    const domain = program.domain;
    
    const weekSubTasks = {
      'fitness': {
        1: [
          ['Warm up for 5 minutes', 'Complete 3 sets of basic exercises', 'Cool down and stretch'],
          ['Focus on proper form', 'Complete 10-15 repetitions', 'Rest between sets'],
          ['Start with 10 minutes of cardio', 'Maintain steady pace', 'Track your heart rate'],
          ['Hold each stretch for 30 seconds', 'Focus on major muscle groups', 'Breathe deeply during stretches'],
          ['Engage your core throughout', 'Complete 3 sets of core exercises', 'Focus on quality over quantity'],
          ['Start with 15 minutes of endurance', 'Maintain consistent effort', 'Monitor your breathing'],
          ['Spend 10 minutes stretching', 'Focus on areas that feel tight', 'Practice deep breathing']
        ],
        2: [
          ['Warm up for 10 minutes', 'Complete 4 sets of intermediate exercises', 'Focus on progressive overload'],
          ['Increase intensity gradually', 'Complete 12-20 repetitions', 'Reduce rest time between sets'],
          ['Complete 20 minutes of cardio', 'Include interval training', 'Monitor heart rate zones'],
          ['Hold stretches for 45 seconds', 'Focus on flexibility and mobility', 'Practice dynamic stretching'],
          ['Engage core with complex movements', 'Complete 4 sets of advanced core exercises', 'Focus on stability and control'],
          ['Complete 25 minutes of endurance training', 'Include tempo variations', 'Track performance metrics'],
          ['Spend 15 minutes on recovery', 'Focus on muscle recovery techniques', 'Practice mindfulness']
        ],
        3: [
          ['Warm up for 15 minutes', 'Complete 5 sets of advanced exercises', 'Focus on power and speed'],
          ['Maximize intensity safely', 'Complete 15-25 repetitions', 'Minimize rest between sets'],
          ['Complete 30 minutes of high-intensity cardio', 'Include advanced interval training', 'Monitor advanced metrics'],
          ['Hold stretches for 60 seconds', 'Focus on advanced flexibility', 'Practice complex mobility patterns'],
          ['Engage core with explosive movements', 'Complete 5 sets of elite core exercises', 'Focus on power and stability'],
          ['Complete 35 minutes of advanced endurance', 'Include race-pace training', 'Track advanced performance metrics'],
          ['Spend 20 minutes on advanced recovery', 'Focus on professional recovery techniques', 'Practice advanced mindfulness']
        ],
        4: [
          ['Warm up for 20 minutes', 'Complete 6 sets of elite exercises', 'Focus on mastery and precision'],
          ['Achieve maximum intensity safely', 'Complete 20-30 repetitions', 'Minimize rest to elite levels'],
          ['Complete 40 minutes of elite cardio', 'Include championship-level intervals', 'Monitor elite performance metrics'],
          ['Hold stretches for 90 seconds', 'Focus on elite flexibility', 'Practice championship mobility'],
          ['Engage core with championship movements', 'Complete 6 sets of championship core exercises', 'Focus on elite power and stability'],
          ['Complete 45 minutes of championship endurance', 'Include championship-pace training', 'Track elite performance metrics'],
          ['Spend 25 minutes on championship recovery', 'Focus on elite recovery techniques', 'Practice championship mindfulness']
        ]
      },
      'programming': {
        1: [
          ['Set up your development environment', 'Install necessary tools', 'Create your first project folder'],
          ['Write 5 simple programs', 'Practice basic syntax', 'Test each program'],
          ['Declare 10 different variables', 'Use appropriate data types', 'Print their values'],
          ['Write if-else statements', 'Create a simple loop', 'Test your logic'],
          ['Write your first function', 'Call the function', 'Test with different inputs'],
          ['Work with strings and numbers', 'Practice type conversion', 'Handle basic operations'],
          ['Organize your code into files', 'Add comments', 'Test everything works']
        ],
        2: [
          ['Set up advanced development tools', 'Configure your IDE', 'Create a structured project'],
          ['Write 10 intermediate programs', 'Practice object-oriented concepts', 'Test with unit tests'],
          ['Create classes and objects', 'Implement inheritance', 'Practice polymorphism'],
          ['Work with arrays and lists', 'Implement sorting algorithms', 'Test performance'],
          ['Handle exceptions properly', 'Write error handling code', 'Test edge cases'],
          ['Read and write files', 'Process file data', 'Handle file operations'],
          ['Debug complex programs', 'Use debugging tools', 'Optimize code performance']
        ],
        3: [
          ['Set up professional development environment', 'Configure advanced tools', 'Create enterprise-level project'],
          ['Write 15 advanced programs', 'Practice design patterns', 'Implement comprehensive testing'],
          ['Implement advanced algorithms', 'Optimize for performance', 'Test with complex data'],
          ['Create database connections', 'Implement CRUD operations', 'Handle database transactions'],
          ['Develop REST APIs', 'Handle HTTP requests', 'Implement authentication'],
          ['Optimize application performance', 'Implement caching strategies', 'Monitor system metrics'],
          ['Implement security best practices', 'Handle sensitive data', 'Test security vulnerabilities']
        ],
        4: [
          ['Set up enterprise development environment', 'Configure production tools', 'Create scalable architecture'],
          ['Write 20 enterprise programs', 'Practice advanced design patterns', 'Implement comprehensive testing suites'],
          ['Implement complex system architectures', 'Design microservices', 'Handle distributed systems'],
          ['Create advanced database systems', 'Implement data warehousing', 'Handle big data processing'],
          ['Develop advanced APIs', 'Implement real-time systems', 'Handle high-concurrency scenarios'],
          ['Optimize for enterprise scale', 'Implement advanced monitoring', 'Handle system reliability'],
          ['Implement enterprise security', 'Handle compliance requirements', 'Test enterprise vulnerabilities']
        ]
      },
      'learning': {
        1: [
          ['Read the introduction chapter', 'Take notes on key concepts', 'Summarize what you learned'],
          ['Practice the basic exercises', 'Complete 3 practice problems', 'Review your answers'],
          ['Watch the foundational video', 'Take notes', 'Try the examples yourself'],
          ['Read about core principles', 'Identify 3 key points', 'Think of real-world applications'],
          ['Practice the essential skills', 'Complete 5 exercises', 'Reflect on your progress'],
          ['Study the building blocks', 'Practice with examples', 'Test your understanding'],
          ['Review all concepts learned', 'Create a summary', 'Plan next steps']
        ],
        2: [
          ['Read the intermediate chapter', 'Take detailed notes', 'Connect concepts to previous learning'],
          ['Complete 5 intermediate exercises', 'Practice problem-solving', 'Review and improve your solutions'],
          ['Watch the intermediate video', 'Take comprehensive notes', 'Practice the advanced examples'],
          ['Study advanced principles', 'Identify 5 key insights', 'Apply to complex scenarios'],
          ['Practice intermediate skills', 'Complete 8 exercises', 'Track your improvement'],
          ['Study intermediate building blocks', 'Practice with complex examples', 'Test your advanced understanding'],
          ['Review all intermediate concepts', 'Create a comprehensive summary', 'Plan advanced learning']
        ],
        3: [
          ['Read the advanced chapter', 'Take expert-level notes', 'Synthesize with all previous learning'],
          ['Complete 8 advanced exercises', 'Practice expert problem-solving', 'Review and optimize your solutions'],
          ['Watch the advanced video', 'Take expert notes', 'Master the complex examples'],
          ['Study expert principles', 'Identify 7 key insights', 'Apply to professional scenarios'],
          ['Practice expert skills', 'Complete 12 exercises', 'Track your mastery progress'],
          ['Study expert building blocks', 'Practice with professional examples', 'Test your expert understanding'],
          ['Review all expert concepts', 'Create a mastery summary', 'Plan professional application']
        ],
        4: [
          ['Read the mastery chapter', 'Take professional notes', 'Synthesize with all learning'],
          ['Complete 12 mastery exercises', 'Practice professional problem-solving', 'Review and perfect your solutions'],
          ['Watch the mastery video', 'Take professional notes', 'Master the professional examples'],
          ['Study mastery principles', 'Identify 10 key insights', 'Apply to leadership scenarios'],
          ['Practice mastery skills', 'Complete 15 exercises', 'Track your leadership progress'],
          ['Study mastery building blocks', 'Practice with leadership examples', 'Test your mastery understanding'],
          ['Review all mastery concepts', 'Create a leadership summary', 'Plan professional leadership']
        ]
      },
      'business': {
        1: [
          ['Research your target market', 'Identify 3 key competitors', 'Note market trends'],
          ['Draft a basic business concept', 'Define your value proposition', 'Identify your target customers'],
          ['Create a simple budget outline', 'List expected expenses', 'Estimate potential revenue'],
          ['Research marketing channels', 'Identify 3 marketing strategies', 'Note costs and benefits'],
          ['Map out your business process', 'Identify key operations', 'Note potential bottlenecks'],
          ['Develop a basic strategy', 'Set 3 key objectives', 'Create action steps'],
          ['Set SMART goals for your business', 'Create a timeline', 'Identify success metrics']
        ],
        2: [
          ['Conduct detailed market analysis', 'Identify 5 key competitors', 'Analyze market trends and opportunities'],
          ['Develop a comprehensive business plan', 'Refine your value proposition', 'Define your customer segments'],
          ['Create a detailed financial model', 'Project cash flow', 'Identify funding requirements'],
          ['Develop a marketing strategy', 'Identify 5 marketing channels', 'Create marketing campaigns'],
          ['Optimize your business processes', 'Identify operational improvements', 'Implement efficiency measures'],
          ['Develop an advanced strategy', 'Set 5 key objectives', 'Create detailed action plans'],
          ['Set advanced business goals', 'Create a comprehensive timeline', 'Identify advanced success metrics']
        ],
        3: [
          ['Conduct comprehensive market research', 'Identify 7 key competitors', 'Analyze market dynamics and opportunities'],
          ['Develop an enterprise business plan', 'Refine your value proposition', 'Define your market segments'],
          ['Create an advanced financial model', 'Project detailed cash flow', 'Identify advanced funding strategies'],
          ['Develop a comprehensive marketing strategy', 'Identify 7 marketing channels', 'Create advanced campaigns'],
          ['Optimize enterprise business processes', 'Identify strategic improvements', 'Implement advanced efficiency measures'],
          ['Develop a strategic business plan', 'Set 7 key objectives', 'Create strategic action plans'],
          ['Set strategic business goals', 'Create a strategic timeline', 'Identify strategic success metrics']
        ],
        4: [
          ['Conduct enterprise market research', 'Identify 10 key competitors', 'Analyze market leadership opportunities'],
          ['Develop a leadership business plan', 'Refine your value proposition', 'Define your leadership segments'],
          ['Create a leadership financial model', 'Project leadership cash flow', 'Identify leadership funding strategies'],
          ['Develop a leadership marketing strategy', 'Identify 10 marketing channels', 'Create leadership campaigns'],
          ['Optimize leadership business processes', 'Identify leadership improvements', 'Implement leadership efficiency measures'],
          ['Develop a leadership business plan', 'Set 10 key objectives', 'Create leadership action plans'],
          ['Set leadership business goals', 'Create a leadership timeline', 'Identify leadership success metrics']
        ]
      }
    };

    const domainSubTasks = weekSubTasks[domain as keyof typeof weekSubTasks] || weekSubTasks['learning'];
    const weekSubTasksArray = domainSubTasks[weekNumber as keyof typeof domainSubTasks] || domainSubTasks[1];
    const tasks = weekSubTasksArray[dayQuestIndex % weekSubTasksArray.length];
    
    return tasks.map((taskText, index) => ({
      id: this.generateId(`subtask_${weekNumber}_${dayQuestIndex}`, index),
      text: taskText,
      completed: false,
      completedAt: null
    }));
  }

  // 🚀 NEW: Get difficulty for week
  private getDifficultyForWeek(weekNumber: number): QuestDifficulty {
    const difficulties: QuestDifficulty[] = ['easy', 'normal', 'hard', 'boss'];
    return difficulties[Math.min(weekNumber - 1, 3)];
  }

  // 🚀 NEW: Get XP reward for week
  private getXPRewardForWeek(weekNumber: number): number {
    const baseXP = [10, 20, 30, 40];
    return baseXP[Math.min(weekNumber - 1, 3)];
  }

  // 🚀 NEW: Get currency reward for week
  private getCurrencyRewardForWeek(weekNumber: number): number {
    const baseCurrency = [5, 10, 15, 20];
    return baseCurrency[Math.min(weekNumber - 1, 3)];
  }

  // 🚀 NEW: Get stat rewards for week
  private getStatRewardsForWeek(weekNumber: number): Partial<Stats> {
    const baseStats = [1, 2, 3, 4];
    const statIncrease = baseStats[Math.min(weekNumber - 1, 3)];
    return {
      strength: statIncrease,
      intelligence: statIncrease,
      agility: statIncrease,
      vitality: statIncrease
    };
  }

  // 🚀 NEW: Check if user is approaching completion of current phase
  shouldGenerateNextPhase(program: Program, userQuests: Quest[]): boolean {
    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    // For AI programs, determine current phase based on quest completion rather than milestones
    let currentPhase: number;
    if (isAIProgram) {
      // Find the earliest phase that has incomplete scheduled quests
      currentPhase = this.getCurrentPhaseForAIProgram(program, userQuests);
    } else {
      // For traditional programs, use date-based logic
      currentPhase = this.getCurrentPhase(program);
    }

    const totalPhases = program.totalPhases;

    // Don't generate if we're already at the last phase
    if (currentPhase >= totalPhases) return false;

    // Get quests for current phase
    const currentPhaseQuests = userQuests.filter(quest => {
      if (!quest.programId || quest.programId !== program.id) return false;

      // Calculate which phase this quest belongs to based on scheduled date
      if (!quest.scheduledFor) return false;

        const questDate = new Date(quest.scheduledFor);
        const startDate = this.getProgramStartDate(program);
        const dayOffset = this.getDaysBetween(startDate, questDate);
        const questPhase = Math.floor(dayOffset / 7) + 1;

      return questPhase === currentPhase;
    });

    // Count completed quests in current phase
    const completedQuests = currentPhaseQuests.filter(quest => quest.status === 'completed');
    const totalQuestsInPhase = currentPhaseQuests.length;

    console.log(`🔍 shouldGenerateNextPhase - currentPhase: ${currentPhase}, totalQuestsInPhase: ${totalQuestsInPhase}, completedQuests: ${completedQuests.length}`);

    // If current phase has no quests yet, generate them immediately
    if (totalQuestsInPhase === 0) {
      console.log(`🔍 shouldGenerateNextPhase - YES (current phase has no quests)`);
      return true;
    }

    // Generate next phase when 66% of current phase is complete (2/3 quests for 3x/week programs)
    // This ensures next week is ready before user finishes current week
    // Using 0.66 instead of 0.67 to account for 2/3 = 0.6666...
    const completionThreshold = 0.66;
    const completionRate = completedQuests.length / totalQuestsInPhase;

    console.log(`🔍 shouldGenerateNextPhase - completionRate: ${completionRate}, threshold: ${completionThreshold}, result: ${completionRate >= completionThreshold}`);

    return completionRate >= completionThreshold;
  }

  // 🚀 NEW: Determine current phase for AI programs based on quest completion
  getCurrentPhaseForAIProgram(program: Program, userQuests: Quest[]): number {
    console.log(`🔍 getCurrentPhaseForAIProgram called for program: ${program.title}`);

    // Start with phase 1
    let currentPhase = 1;

    // Check each phase to see if it's completed (all scheduled quests done)
    for (let phase = 1; phase <= program.totalPhases; phase++) {
      const phaseQuests = userQuests.filter(quest => {
        if (!quest.programId || quest.programId !== program.id) return false;
        if (!quest.scheduledFor) return false;

        const questDate = new Date(quest.scheduledFor);
        const startDate = this.getProgramStartDate(program);
        const dayOffset = this.getDaysBetween(startDate, questDate);
        const questPhase = Math.floor(dayOffset / 7) + 1;

        return questPhase === phase;
      });

      console.log(`🔍 Phase ${phase}: ${phaseQuests.length} quests found`);
      const completedQuests = phaseQuests.filter(quest => quest.status === 'completed');
      console.log(`🔍 Phase ${phase}: ${completedQuests.length}/${phaseQuests.length} quests completed`);

      // If this phase has quests and not all are completed, this is the current phase
      if (phaseQuests.length > 0) {
        if (completedQuests.length < phaseQuests.length) {
          currentPhase = phase;
          console.log(`🔍 Current phase set to ${currentPhase} (incomplete quests found)`);
          break;
        } else {
          console.log(`🔍 Phase ${phase} is fully completed`);
          // Continue to next phase - if next phase has no quests, it means we should generate them
          currentPhase = phase + 1;
        }
      } else {
        // No quests found for this phase - this is the phase we should generate
        currentPhase = phase;
        console.log(`🔍 Current phase set to ${currentPhase} (no quests found, need to generate)`);
        break;
      }
    }

    const finalPhase = Math.min(currentPhase, program.totalPhases);
    console.log(`🔍 Final current phase for AI program: ${finalPhase}`);
    return finalPhase;
  }

  // Generate today's quests from all active programs
  generateTodayQuests(programs: Program[], userId: string, date: Date = new Date()): Quest[] {
    const todayQuests: Quest[] = [];

    programs.forEach(program => {
      if (!this.shouldGenerateToday(program, date)) return;

      const currentPhase = this.getCurrentPhase(program);
      
      // Get quest templates for current phase
      const phaseTemplates = program.questTemplates.filter(qt => 
        qt.phase === currentPhase
      );

      // Limit to questsPerDay
      const templatesToGenerate = phaseTemplates.slice(0, program.schedule.questsPerDay);

      // Create actual quests from templates
      templatesToGenerate.forEach((template, index) => {
        const quest = this.createQuestFromTemplate(template, program, userId, index);
        todayQuests.push(quest);
      });
    });

    return todayQuests;
  }

  // Create a Quest from a QuestTemplate
  private createQuestFromTemplate(
    template: QuestTemplate,
    program: Program,
    userId: string,
    index: number = 0,
    scheduledFor?: string
  ): Quest {
    // Convert template subTasks (string[]) to Quest subTasks (SubTask[])
    const subTasks = template.subTasks?.map((taskText, idx) => ({
      id: this.generateId(`subtask_${index}`, idx),
      text: taskText,
      completed: false
    }));

    return {
      id: this.generateId(`program_${program.id}`, index),
      userId,
      title: template.title,
      description: template.description,
      type: template.type,
      difficulty: template.difficulty,
      xpReward: getXPRewardForDifficulty(template.difficulty),
      currencyReward: getCurrencyRewardForDifficulty(template.difficulty),
      statRewards: getStatRewardsForDifficulty(template.difficulty),
      status: 'active',
      createdAt: Date.now() + index,
      completedAt: null,
      resetDaily: false, // Program quests don't reset - they're one-time
      scheduledFor: scheduledFor, // Schedule quest for specific date
      subTasks,
      // Include enhanced details from template (resources deprecated)
      resources: undefined, // Removed - often inaccurate
      programId: program.id, // Link back to the program
    };
  }

  // Check and complete milestones
  checkMilestones(program: Program, questGetter: () => Quest[]): Milestone[] {
    const quests = questGetter(); // Get fresh quest data
    // Use universal phase detection (handles both AI and traditional programs correctly)
    const currentPhase = this.getCurrentPhaseUniversal(program, quests);
    const completedMilestones: Milestone[] = [];

    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    program.milestones.forEach(milestone => {
      if (milestone.completed) return; // Already completed

      let shouldComplete = false;

      if (isAIProgram) {
        // For AI programs, check if all quests in this phase are completed
        if (quests.length > 0) {
          const programQuests = quests.filter(quest => quest.programId === program.id);

          const phaseQuests = programQuests.filter(quest => {
            if (!quest.scheduledFor) return false;

            // Calculate which phase this quest belongs to
            const questDate = new Date(quest.scheduledFor);
            const startDate = this.getProgramStartDate(program);
            const dayOffset = this.getDaysBetween(startDate, questDate);
            const questPhase = Math.floor(dayOffset / 7) + 1;

            return questPhase === milestone.phase;
          });

          const completedPhaseQuests = phaseQuests.filter(quest => quest.status === 'completed');
          
          // Complete milestone ONLY when:
          // 1. All quests in this phase exist AND are completed
          // 2. OR we've moved past this phase (currentPhase > milestone.phase) AND phase had quests
          if (phaseQuests.length > 0 && completedPhaseQuests.length === phaseQuests.length) {
            shouldComplete = true;
          } else if (milestone.phase < currentPhase && phaseQuests.length > 0) {
            // If we're past this phase and it had quests, complete it
            shouldComplete = true;
          }
        }
      } else {
        // For traditional programs, use date-based logic:
        // Complete milestone if we've moved past that phase OR program is completed
        shouldComplete = milestone.phase < currentPhase ||
                         (program.status === 'completed' && milestone.phase <= currentPhase);
      }

      if (shouldComplete) {
        milestone.completed = true;
        milestone.completedAt = Date.now();
        completedMilestones.push(milestone);
      }
    });

    return completedMilestones;
  }

  // Get next milestone for a program
  getNextMilestone(program: Program): Milestone | null {
    return program.milestones.find(m => !m.completed) || null;
  }

  // Calculate days remaining in program
  getDaysRemaining(program: Program): number {
    const today = new Date();
    const endDate = new Date(program.endDate);
    const diff = endDate.getTime() - today.getTime();
    // 🐛 FIX: Use Math.round for balanced countdown behavior
    // Provides consistent display without showing 0 days on the final partial day
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
  }

  // Get readable schedule description
  getScheduleDescription(schedule: ProgramSchedule): string {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    switch (schedule.type) {
      case 'daily':
        return 'Every day';
      case 'every_other_day':
        return 'Every other day';
      case '2x_week':
        return 'Mon, Thu';
      case '3x_week':
        return 'Mon, Wed, Fri';
      case '5x_week':
        return 'Weekdays';
      case 'weekends':
        return 'Weekends';
      case 'custom':
        if (schedule.customDays) {
          return schedule.customDays.map(d => dayNames[d]).join(', ');
        }
        return 'Custom';
      default:
        return 'Not set';
    }
  }

  // Pause a program
  pauseProgram(program: Program): Program {
    return {
      ...program,
      status: 'paused',
      pausedAt: Date.now(),
    };
  }

  // Resume a paused program
  resumeProgram(program: Program): Program {
    if (program.status !== 'paused' || !program.pausedAt) return program;

    // Calculate how many days the program was paused
    const pausedDuration = Date.now() - program.pausedAt;
    const pausedDays = Math.floor(pausedDuration / (1000 * 60 * 60 * 24));

    // Extend the end date by the paused duration
    const newEndDate = new Date(program.endDate);
    newEndDate.setDate(newEndDate.getDate() + pausedDays);

    return {
      ...program,
      status: 'active',
      endDate: newEndDate.toISOString().split('T')[0],
      pausedAt: null,
    };
  }

  // Mark program as completed
  completeProgram(program: Program): Program {
    return {
      ...program,
      status: 'completed',
      progress: 100,
      completedAt: Date.now(),
    };
  }

  // Calculate expected quests for AI programs
  calculateExpectedQuestsForAIProgram(program: Program): number {
    const { schedule, availableDays } = program;
    
    // 🔧 FIX: Recalculate totalPhases from duration to handle old programs with incorrect phase count
    // Use Math.floor to match the server-side calculation (30 days = 4 weeks, not 5)
    const startDate = new Date(program.startDate);
    const endDate = new Date(program.endDate);
    const durationInDays = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    const correctTotalPhases = Math.max(1, Math.floor(durationInDays / 7));
    
    console.log(`📊 calculateExpectedQuests - duration: ${durationInDays} days, correctPhases: ${correctTotalPhases}, storedPhases: ${program.totalPhases}`);
    
    if (!availableDays || availableDays.length === 0) {
      // Fallback: estimate based on schedule type
      let daysPerWeek = 7;
      switch (schedule.type) {
        case 'daily': daysPerWeek = 7; break;
        case '5x_week': daysPerWeek = 5; break;
        case '3x_week': daysPerWeek = 3; break;
        case '2x_week': daysPerWeek = 2; break;
        case 'weekends': daysPerWeek = 2; break;
        case 'every_other_day': daysPerWeek = 3.5; break;
        default: daysPerWeek = 7;
      }
      return Math.ceil(correctTotalPhases * daysPerWeek * schedule.questsPerDay);
    }
    
    // Calculate based on available days
    const questsPerPhase = availableDays.length * schedule.questsPerDay;
    const expected = correctTotalPhases * questsPerPhase;
    console.log(`📊 Expected quests: ${correctTotalPhases} weeks × ${questsPerPhase} quests/week = ${expected} total`);
    return expected;
  }

  // Get program statistics
  getProgramStats(program: Program): {
    totalQuestsExpected: number;
    completionRate: number;
    daysActive: number;
    milestonesCompleted: number;
  } {
    const today = new Date();
    const startDate = this.getProgramStartDate(program);
    const daysActive = this.getDaysBetween(startDate, today) + 1; // +1 to include today

    // Determine if this is an AI program
    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    // For AI programs, use totalQuestsExpected if set, otherwise calculate it
    // For traditional programs, use questsGenerated
    const totalQuestsExpected = isAIProgram 
      ? (program.totalQuestsExpected || this.calculateExpectedQuestsForAIProgram(program))
      : (program.questsGenerated || 0);

    const completionRate = totalQuestsExpected > 0
      ? (program.questsCompleted / totalQuestsExpected) * 100
      : 0;

    const milestonesCompleted = program.milestones.filter(m => m.completed).length;

    return {
      totalQuestsExpected,
      completionRate: Math.min(100, completionRate),
      daysActive: Math.max(1, daysActive), // At least 1 day active
      milestonesCompleted,
    };
  }
}

export const programService = new ProgramService();

